import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'

interface FileDropZoneProps {
  onFilesSelected: (files: FileList) => void
  accept?: string
  disabled?: boolean
}

export default function FileDropZone({
  onFilesSelected,
  accept = '.pdf,.jpg,.jpeg,.png,.gif,.svg,.webp,.eml,.msg',
  disabled = false,
}: FileDropZoneProps) {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled) return

    const files = e.dataTransfer.files
    if (files.length > 0) {
      onFilesSelected(files)
    }
  }

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      onFilesSelected(files)
      // Reset input so same file can be selected again
      e.target.value = ''
    }
  }

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative border-2 border-dashed rounded-lg p-8
        flex flex-col items-center justify-center
        cursor-pointer transition-all
        ${isDragging ? 'border-primary bg-primary/10 scale-[1.02]' : 'border-base-300 hover:border-primary/50 hover:bg-base-200/50'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      data-testid="file-drop-zone"
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
        data-testid="file-input"
      />

      <Upload className={`w-12 h-12 mb-4 ${isDragging ? 'text-primary' : 'text-base-content/40'}`} />

      <p className="text-lg font-medium text-base-content mb-2">
        {isDragging ? t('documents.dropZone.dropHere') : t('documents.dropZone.dropOrClick')}
      </p>

      <p className="text-sm text-base-content/60">
        {t('documents.dropZone.fileTypes')}
      </p>
    </div>
  )
}
